import axios from 'axios';
import { supabase } from '../supabaseClient';
import { apiEndpoints, API_BASE_URL } from '../config/api';

export interface User {
    id: string;
    email: string;
    role: string;
    created_at: string;
    last_sign_in_at?: string;
}

export interface CreateUserRequest {
    email: string;
    first_name?: string;
    last_name?: string;
    role?: 'admin' | 'user';
    send_invitation?: boolean;
    password?: string;
    account_id?: string; // Optional account to assign user to
    account_role?: 'owner' | 'admin' | 'member'; // Role within the account
}

export interface CreateUserResponse {
    user: {
        id: string;
        email: string;
        first_name?: string;
        last_name?: string;
        role: string;
        email_confirmed: boolean;
        created_at: string;
    };
    invitation_sent: boolean;
    account_assigned?: {
        account_id: string;
        role: string;
        success: boolean;
    };
}

export interface AccountOption {
    id: string;
    name: string;
    plan_type: string;
    is_active: boolean;
}

export interface UpdateUserRequest {
    first_name?: string;
    last_name?: string;
    role?: 'admin' | 'user';
    is_active?: boolean;
}

export async function getCurrentUserRole(): Promise<{ role: string | null; error?: string }> {
    try {
        const { data: { user } } = await supabase.auth.getUser();

        if (!user) {
            return { role: null, error: 'Not authenticated' };
        }

        // First try to get role from the backend (requires service key)
        try {
            const response = await axios.get(`${apiEndpoints.userRole}?userId=${user.id}`);
            return { role: response.data.role };
        } catch (backendError) {
            console.warn('Backend role check failed, using fallback method:', backendError);

            // Fallback: get role from current user's metadata (ignore system role)
            const role = user.user_metadata?.role || 'user';

            console.log('Frontend role fallback:', {
                system_role: (user as any)?.role,                  // System role (authenticated/anon)
                custom_role: user.user_metadata?.role,             // Custom app role (admin/user)
                final_role: role,
                full_user_metadata: user.user_metadata
            });

            return { role };
        }
    } catch (error: any) {
        return { role: null, error: error.message || 'Failed to get user role' };
    }
}

export async function getAllUsers(): Promise<{ users: User[] | null; error?: string }> {
    try {
        const { data: { user } } = await supabase.auth.getUser();

        if (!user) {
            return { users: null, error: 'Not authenticated' };
        }

        const response = await axios.get(`${apiEndpoints.adminUsers}?currentUserId=${user.id}`);

        return { users: response.data.users };
    } catch (error: any) {
        return { users: null, error: error.response?.data?.error || 'Failed to fetch users' };
    }
}

export async function createUser(userRequest: CreateUserRequest): Promise<{ data: CreateUserResponse | null; error?: string }> {
    try {
        const { data: { user } } = await supabase.auth.getUser();

        if (!user) {
            return { data: null, error: 'Not authenticated' };
        }

        const response = await axios.post(apiEndpoints.adminUsers, {
            ...userRequest,
            currentUserId: user.id
        });

        return { data: response.data };
    } catch (error: any) {
        const errorMessage = error.response?.data?.error || 'Failed to create user';
        const errorCode = error.response?.data?.code;

        return {
            data: null,
            error: errorCode === 'user_already_exists'
                ? 'A user with this email address already exists'
                : errorMessage
        };
    }
}

export async function updateUser(userId: string, updates: UpdateUserRequest): Promise<{ success: boolean; error?: string }> {
    try {
        const { data: { user } } = await supabase.auth.getUser();

        if (!user) {
            return { success: false, error: 'Not authenticated' };
        }

        const response = await axios.put(`${apiEndpoints.adminUsers}/${userId}`, {
            ...updates,
            currentUserId: user.id
        });

        return { success: response.data.success };
    } catch (error: any) {
        return { success: false, error: error.response?.data?.error || 'Failed to update user' };
    }
}

export async function deleteUser(userId: string): Promise<{ success: boolean; error?: string }> {
    try {
        const { data: { user } } = await supabase.auth.getUser();

        if (!user) {
            return { success: false, error: 'Not authenticated' };
        }

        const response = await axios.delete(`${apiEndpoints.adminUsers}/${userId}`, {
            data: { currentUserId: user.id }
        });

        return { success: response.data.success };
    } catch (error: any) {
        return { success: false, error: error.response?.data?.error || 'Failed to delete user' };
    }
}

export async function resendInvitation(userId: string, email: string): Promise<{ success: boolean; error?: string }> {
    try {
        const { data: { user } } = await supabase.auth.getUser();

        if (!user) {
            return { success: false, error: 'Not authenticated' };
        }

        const response = await axios.post(`${apiEndpoints.adminUsers}/${userId}/resend-invitation`, {
            email,
            currentUserId: user.id
        });

        return { success: response.data.success };
    } catch (error: any) {
        return { success: false, error: error.response?.data?.error || 'Failed to resend invitation' };
    }
}

export async function getAvailableAccounts(): Promise<{ accounts: AccountOption[] | null; error?: string }> {
    try {
        const { data: { user } } = await supabase.auth.getUser();

        if (!user) {
            return { accounts: null, error: 'Not authenticated' };
        }

        const response = await axios.get(`${apiEndpoints.adminAccounts}?currentUserId=${user.id}`);

        return { accounts: response.data.accounts };
    } catch (error: any) {
        return { accounts: null, error: error.response?.data?.error || 'Failed to fetch accounts' };
    }
}

export async function updateUserRole(userId: string, role: string): Promise<{ success: boolean; error?: string }> {
    try {
        const { data: { user } } = await supabase.auth.getUser();

        if (!user) {
            return { success: false, error: 'Not authenticated' };
        }

        const response = await axios.put(`${apiEndpoints.adminUsers}/${userId}/role`, {
            role,
            currentUserId: user.id
        });

        return { success: response.data.success };
    } catch (error: any) {
        return { success: false, error: error.response?.data?.error || 'Failed to update user role' };
    }
}

export async function addUserToAccount(
    accountId: string,
    userEmail: string,
    role: 'owner' | 'admin' | 'member' = 'member'
): Promise<{ success: boolean; error?: string; user?: any }> {
    try {
        const { data: { user } } = await supabase.auth.getUser();

        if (!user) {
            return { success: false, error: 'Not authenticated' };
        }

        const response = await axios.post(`${API_BASE_URL}/accounts/${accountId}/users`, {
            userEmail,
            role,
            currentUserId: user.id
        });

        return { success: response.data.success, user: response.data.user };
    } catch (error: any) {
        return { success: false, error: error.response?.data?.error || 'Failed to add user to account' };
    }
}

export async function removeUserFromAccount(
    accountId: string,
    userId: string
): Promise<{ success: boolean; error?: string }> {
    try {
        const { data: { user } } = await supabase.auth.getUser();

        if (!user) {
            return { success: false, error: 'Not authenticated' };
        }

        const response = await axios.delete(`${API_BASE_URL}/accounts/${accountId}/users/${userId}`, {
            data: { currentUserId: user.id }
        });

        return { success: response.data.success };
    } catch (error: any) {
        return { success: false, error: error.response?.data?.error || 'Failed to remove user from account' };
    }
}

export interface UpdateAccountRequest {
    name?: string;
    plan_type?: 'starter' | 'essentials' | 'pro' | 'enterprise';
    plan_status?: 'active' | 'canceled' | 'past_due' | 'trialing';
    is_active?: boolean;
}

export async function updateAccount(
    accountId: string,
    updates: UpdateAccountRequest
): Promise<{ success: boolean; account?: any; error?: string }> {
    try {
        const { data: { user } } = await supabase.auth.getUser();

        if (!user) {
            return { success: false, error: 'Not authenticated' };
        }

        const response = await axios.put(`${API_BASE_URL}/admin/accounts/${accountId}`, {
            ...updates,
            currentUserId: user.id
        });

        return { success: response.data.success, account: response.data.account };
    } catch (error: any) {
        return { success: false, error: error.response?.data?.error || 'Failed to update account' };
    }
}

export async function updateUserAccountRole(
    accountId: string,
    userId: string,
    role: 'owner' | 'admin' | 'member'
): Promise<{ success: boolean; error?: string }> {
    try {
        const { data: { user } } = await supabase.auth.getUser();

        if (!user) {
            return { success: false, error: 'Not authenticated' };
        }

        const response = await axios.put(`${API_BASE_URL}/accounts/${accountId}/users/${userId}/role`, {
            role,
            currentUserId: user.id
        });

        return { success: response.data.success };
    } catch (error: any) {
        return { success: false, error: error.response?.data?.error || 'Failed to update user account role' };
    }
} 